import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle, AlertCircle, Loader2, Key } from 'lucide-react';
import { FloatingParticles } from '../../components/animations/FloatingParticles';
import apiClient from '../../utils/apiClient';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('email', email);

      await apiClient.post('/auth/forgot-password', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccess(true);
    } catch (error: any) {
      // Check if email is not registered (404 error)
      if (error.response?.status === 404) {
        setError('This email is not registered. Please check your email or register for a new account.');
      } else if (error.response?.data?.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Failed to process password reset request. Please try again.');
      }
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      <FloatingParticles />
      
      {/* Background gradient effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-black to-yellow-900/20 pointer-events-none" />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl pointer-events-none animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl pointer-events-none animate-pulse delay-1000" />
      
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="max-w-md w-full"
        >
          <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl sm:rounded-3xl p-8 sm:p-10 shadow-2xl hover:shadow-amber-500/10 hover:bg-white/[0.07] hover:border-white/15 transition-all duration-300">
            
            {/* Header */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="w-20 h-20 bg-gradient-to-br from-amber-500/30 to-yellow-500/30 backdrop-blur-xl rounded-full flex items-center justify-center mx-auto mb-6 border border-amber-400/30 shadow-xl"
              >
                {success ? (
                  <CheckCircle className="w-10 h-10 text-green-300" />
                ) : (
                  <Key className="w-10 h-10 text-amber-300" />
                )}
              </motion.div>
              
              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-white via-amber-100 to-yellow-200 bg-clip-text text-transparent mb-3 leading-tight"
              >
                {success ? 'Check Your Email' : 'Reset Password'}
              </motion.h2>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-gray-300 text-lg"
              >
                {success 
                  ? 'We\'ve sent password reset instructions to your email address.'
                  : 'Enter your email address and we\'ll send you a link to reset your password.'}
              </motion.p>
            </div>

            {/* Success State */}
            {success ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-center py-8"
              >
                <motion.div
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-2xl p-8 mb-8 border border-green-400/20 backdrop-blur-xl"
                >
                  <CheckCircle className="w-20 h-20 text-green-400 mx-auto mb-6" />
                  <h3 className="text-2xl font-semibold text-white mb-4">
                    Reset Link Sent!
                  </h3>
                  <p className="text-gray-300 text-lg leading-relaxed mb-4">
                    Check your email for instructions on how to reset your password.
                  </p>
                  <p className="text-sm text-gray-400">
                    Didn't receive an email? Check your spam folder or try again.
                  </p>
                </motion.div>
                
                <div className="flex flex-col gap-4">
                  <motion.button
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSuccess(false)}
                    className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black py-4 px-6 rounded-xl font-semibold transition-all shadow-lg hover:shadow-amber-500/25"
                  >
                    Send Another Email
                  </motion.button>
                  
                  <Link 
                    to="/login"
                    className="bg-white/5 hover:bg-white/10 text-white py-4 px-6 rounded-xl font-semibold transition-all border border-white/10 hover:border-white/20 text-center"
                  >
                    Back to Login
                  </Link>
                </div>
              </motion.div>
            ) : (
              /* Form State */
              <motion.form 
                onSubmit={handleSubmit}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="space-y-6"
              >
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-300">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-400/50 transition-all backdrop-blur-xl"
                      disabled={loading}
                      required
                    />
                  </div>
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-red-900/20 border border-red-500/30 text-red-300 p-4 rounded-xl backdrop-blur-xl"
                  >
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="mb-3">{error}</p>
                        {error.includes('not registered') && (
                          <Link
                            to="/register"
                            className="inline-block bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-black px-4 py-2 rounded-lg font-semibold text-sm transition-all"
                          >
                            Register Now
                          </Link>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}

                <motion.button
                  type="submit"
                  disabled={loading || !email.trim()}
                  whileHover={{ scale: loading ? 1 : 1.02, y: loading ? 0 : -2 }}
                  whileTap={{ scale: loading ? 1 : 0.98 }}
                  className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 disabled:from-gray-600 disabled:to-gray-700 text-black disabled:text-gray-400 py-4 px-6 rounded-xl font-semibold transition-all flex items-center justify-center gap-3 shadow-lg hover:shadow-amber-500/25 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending Reset Link...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </motion.button>
              </motion.form>
            )}
            
            {/* Navigation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-center mt-8 pt-6 border-t border-white/10"
            >
              <Link 
                to="/login"
                className="inline-flex items-center gap-2 text-gray-300 hover:text-amber-400 transition-colors font-semibold"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;